import type { MoneyFormatter } from "@slot/game-shell";
import { multiplyMinorByMultiplier } from "@slot/game-shell";

/**
 * Hardcoded paytable spec (per-symbol LINE pays). Source of truth for the bet-aware values
 * rendered in the info-page paytable.
 *
 * REELS model: a win is N matching symbols on a payline, LEFTMOST-ANCHORED (from reel 1
 * rightwards) on the 5×5 grid's 19 paylines. N is exactly 3, 4 or 5 — five reels is the longest
 * possible run, so there is no open-ended top tier (contrast the cascade model this replaced,
 * which paid on cluster COUNT with an 8+/10+/12+ ladder).
 *
 * Multipliers are × TOTAL BET and awarded PER LINE; multiple winning lines add together. On a
 * $1 total bet the values ARE the dollar payouts for that line (5×H4 = $12.00).
 *
 * The paytable is shared across every reel set (default / vs_rush / super_bonus / bonus_hunt /
 * guaranteed_vs) — only the strips and the battle economy differ per context.
 *
 * Special symbols carry NO line pay of their own and are deliberately absent here:
 *   W  — wild; substitutes for any paying symbol (H1–H4, L1–L5).
 *   VS — battle wild; expands its column to wild. A winning line CROSSING it is multiplied by
 *        the sum of the winners it crosses (additive across columns).
 *   FS — scatter; never substitutes, never pays a line, and BREAKS THE RUN (a line stops at the
 *        reel before it). Triggers the free-spin sessions.
 *
 * Values mirror the certified math model — par sheet `archive/par-2026-07-05-347c2ad4-rtp96.md`
 * §3, held OUTSIDE this repo. Keep in sync manually if the math changes; drift is a known
 * tradeoff (the info page is static, rare-change reference data) and compliance review catches
 * mismatches.
 */

export type PaytableTierSpec = {
  /** Exact matching-symbol count on the payline (3, 4 or 5). */
  matchCount: number;
  /** Win multiplier applied to the TOTAL bet for that line (lineWin = totalBet × multiplier). */
  multiplier: number;
};

export type PaytableRowSpec = {
  /** Symbol identifier (matches frame names in `info/paytable/symbols.plist`). */
  symbolId: string;
  /** Per-tier specs in descending order (5-of-a-kind first). */
  tiers: readonly PaytableTierSpec[];
};

/**
 * The 9 paying symbols (H1–H4, L1–L5), ordered descending by 5-of-a-kind value — the order the
 * info-page paytable renders top-to-bottom.
 *
 * L1–L5 are DELIBERATELY identical (0.25 / 0.50 / 1.00): five interchangeable low tiles, so the
 * only differentiation on the board is H1–H4. The base ladder is intentionally small — the
 * biggest possible base line win is 5×H4 = 12× — because the economy lives in the VS battle
 * multipliers and the Super Bonus savedTotal ratchet, which is how a 12×-max paytable reaches
 * the 25,000× cap.
 */
export const PAYTABLE_SPEC: readonly PaytableRowSpec[] = [
  { symbolId: "H4", tiers: [
    { matchCount: 5, multiplier: 12 },
    { matchCount: 4, multiplier: 5 },
    { matchCount: 3, multiplier: 2 },
  ]},
  { symbolId: "H3", tiers: [
    { matchCount: 5, multiplier: 6 },
    { matchCount: 4, multiplier: 2.5 },
    { matchCount: 3, multiplier: 1.2 },
  ]},
  { symbolId: "H2", tiers: [
    { matchCount: 5, multiplier: 3 },
    { matchCount: 4, multiplier: 1.6 },
    { matchCount: 3, multiplier: 0.8 },
  ]},
  { symbolId: "H1", tiers: [
    { matchCount: 5, multiplier: 2 },
    { matchCount: 4, multiplier: 1.2 },
    { matchCount: 3, multiplier: 0.5 },
  ]},
  { symbolId: "L1", tiers: [
    { matchCount: 5, multiplier: 1 },
    { matchCount: 4, multiplier: 0.5 },
    { matchCount: 3, multiplier: 0.25 },
  ]},
  { symbolId: "L2", tiers: [
    { matchCount: 5, multiplier: 1 },
    { matchCount: 4, multiplier: 0.5 },
    { matchCount: 3, multiplier: 0.25 },
  ]},
  { symbolId: "L3", tiers: [
    { matchCount: 5, multiplier: 1 },
    { matchCount: 4, multiplier: 0.5 },
    { matchCount: 3, multiplier: 0.25 },
  ]},
  { symbolId: "L4", tiers: [
    { matchCount: 5, multiplier: 1 },
    { matchCount: 4, multiplier: 0.5 },
    { matchCount: 3, multiplier: 0.25 },
  ]},
  { symbolId: "L5", tiers: [
    { matchCount: 5, multiplier: 1 },
    { matchCount: 4, multiplier: 0.5 },
    { matchCount: 3, multiplier: 0.25 },
  ]},
] as const;

/**
 * Builds the token map injected into the info-page HTML at parse time.
 *
 * One token per (symbolId, matchCount) pair: `paytable.<symbol>.tier<N>` where N is 3, 4 or 5.
 * Values are formatted money strings (currency-aware) computed from the current TOTAL bet × the
 * tier multiplier — i.e. what ONE winning line of that length pays at the current bet.
 *
 * Example output at bet "100" (=$1.00) with a USD formatter:
 * ```
 * {
 *   "paytable.H4.tier5": "$12.00",   // 100 × 12
 *   "paytable.H4.tier4": "$5.00",
 *   "paytable.H4.tier3": "$2.00",
 *   ...
 *   "paytable.L5.tier3": "$0.25",
 * }
 * ```
 *
 * Consumed by `<td>{paytable.H4.tier5}</td>` references in the HTML.
 */
export function buildPaytableTokens(input: {
  betMinor: string;
  formatter: MoneyFormatter;
}): Record<string, string> {
  const tokens: Record<string, string> = {};
  for (const row of PAYTABLE_SPEC) {
    for (const tier of row.tiers) {
      const winMinor = multiplyMinorByMultiplier(input.betMinor, tier.multiplier);
      tokens[`paytable.${row.symbolId}.tier${tier.matchCount}`] = input.formatter.formatMinor(winMinor);
    }
  }
  return tokens;
}
